import { useState } from 'react'
import { searchClauses, type ClauseHit } from '../api'
import { copyToClipboard, insertTextAtCursor } from '../word'
import { t } from '../i18n'

interface Props {
  token: string
}

function kindLabel(kind: string): string {
  if (kind === 'required_clause') return t('обовʼязкова клауза')
  if (kind === 'forbidden') return t('заборона')
  if (kind === 'constraint') return t('обмеження')
  return kind
}

export function ClauseSearch({ token }: Props) {
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [playbookHits, setPlaybookHits] = useState<ClauseHit[]>([])
  const [expanded, setExpanded] = useState<string | null>(null)
  const [status, setStatus] = useState<string | null>(null)

  async function search(e: React.FormEvent) {
    e.preventDefault()
    const query = q.trim()
    if (query.length < 1) return
    setBusy(true)
    setError(null)
    setStatus(null)
    try {
      const clauses = await searchClauses({ token, q: query })
      setPlaybookHits(clauses.hits)
      if (clauses.hits.length === 0) {
        setStatus(t('Нічого не знайдено. Додайте затверджені формулювання в плейбук.'))
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Пошук не вдався'))
    } finally {
      setBusy(false)
    }
  }

  async function insert(text: string) {
    setStatus(null)
    const ok = await insertTextAtCursor(text, true)
    if (ok) {
      setStatus(t('Вставлено в документ зі змінами, що відстежуються.'))
      return
    }
    const copied = await copyToClipboard(text)
    setStatus(
      copied
        ? t('Не вдалося вставити. Текст скопійовано — вставте вручну.')
        : t('Не вдалося вставити формулювання.'),
    )
  }

  return (
    <div className="clause-search">
      <p className="lead">{t('Шукайте затверджені формулювання плейбука і вставляйте в договір.')}</p>
      <form className="card" onSubmit={(e) => void search(e)}>
        <label>{t('Пошук клаузи')}<input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('конфіденційність, оплата, відповідальність')}
            aria-label={t('Запит пошуку клаузи')}
          />
        </label>
        <button type="submit" disabled={busy}>
          {busy ? t('Шукаю…') : t('Шукати')}
        </button>
      </form>
      {error ? <p className="error">{error}</p> : null}
      {status ? <p className="muted">{status}</p> : null}

      {playbookHits.length > 0 ? (
        <section>
          <h2 className="group-head">{t('Плейбук ({n})', { n: playbookHits.length })}</h2>
          <ul className="finding-list">
            {playbookHits.map((hit) => {
              const key = `${hit.rule_id}:${hit.text.slice(0, 24)}`
              const open = expanded === key
              return (
                <li key={key} className="card">
                  <div className="finding-head">
                    <strong>{hit.title}</strong>
                    <span className="badge">{t('шаблон')}</span>
                  </div>
                  <p className="muted small">
                    {hit.contract_type} · {kindLabel(hit.kind)}
                  </p>
                  <p className={open ? 'justification' : 'change-summary'}>
                    {open ? hit.text : `${hit.text.slice(0, 180)}${hit.text.length > 180 ? '…' : ''}`}
                  </p>
                  <div className="row wrap">
                    <button type="button" className="secondary" onClick={() => setExpanded(open ? null : key)}>
                      {open ? t('Згорнути') : t('Показати все')}
                    </button>
                    <button type="button" onClick={() => void insert(hit.text)}>{t('Вставити')}</button>
                  </div>
                </li>
              )
            })}
          </ul>
        </section>
      ) : null}
    </div>
  )
}
